import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Heart, 
  Brain, 
  DollarSign,
  Users, 
  GraduationCap, 
  Briefcase,
  Clock,
  TrendingUp,
  Target,
  Play,
  Pause,
  RotateCcw,
  X,
  Zap,
  Star,
  Award,
  ChevronRight
} from 'lucide-react';
import { useGameStore } from '../store/gameStore';
import DecisionCard from './DecisionCard';
import { useGameLogic } from '../hooks/useGameLogic';
import { useTimeManagement } from '../hooks/useTimeManagement';
import CharacterAvatar from './CharacterAvatar';
import FinancialManagement from './FinancialManagement';
import NotificationCenter from './NotificationCenter';
import StatusPanel from './StatusPanel';
import EventModal from './EventModal';
import ProfessionalTimeline from './ProfessionalTimeline';

const NewDashboard: React.FC = () => {
  const { 
    currentAge, 
    currentStage, 
    currentMonth,
    stats, 
    status,
    money,
    availableDecisions, 
    mandatoryDecisions,
    decisions,
    lifeStages,
    score,
    timeline,
    gamePhase,
    pauseGame,
    resumeGame,
    resetGame,
    ensureDecisionsAvailable,
    skipMandatoryDecision,
    isTimeRunning,
    timeSpeed,
    startTime,
    pauseTime,
    forwardOneYear,
  } = useGameStore();

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentEvent, setCurrentEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);

  // Initialize game logic and time management
  useGameLogic();
  useTimeManagement();

  // Listen for interactive events
  useEffect(() => {
    const handleShowEvent = (event: CustomEvent) => {
      setCurrentEvent(event.detail);
      setShowEventModal(true);
    };

    window.addEventListener('showEvent', handleShowEvent as EventListener);
    return () => {
      window.removeEventListener('showEvent', handleShowEvent as EventListener);
    };
  }, []);

  const currentStageData = lifeStages.find(stage => stage.id === currentStage);
  const availableDecisionsData = decisions.filter(d => availableDecisions.includes(d.id));
  const mandatoryDecisionsData = decisions.filter(d => mandatoryDecisions.includes(d.id));

  const getStatIcon = (stat: string) => {
    const icons = {
      health: Heart,
      happiness: Brain,
      wealth: DollarSign,
      relationships: Users,
      education: GraduationCap,
      career: Briefcase,
    };
    return icons[stat as keyof typeof icons] || Heart;
  };

  const getStatColor = (value: number) => {
    if (value >= 80) return 'text-emerald-400';
    if (value >= 60) return 'text-amber-400';
    return 'text-red-400';
  };

  const getStatBgColor = (value: number) => {
    if (value >= 80) return 'bg-emerald-50 border-emerald-200';
    if (value >= 60) return 'bg-amber-50 border-amber-200';
    return 'bg-red-50 border-red-200';
  };

  const filteredDecisions = selectedCategory === 'all' 
    ? availableDecisionsData 
    : availableDecisionsData.filter(d => d.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Top Navigation Bar */}
      <div className="bg-slate-800/50 backdrop-blur-xl border-b border-slate-700/50 sticky top-0 z-40">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Age & Month Display - Left */}
            <div className="flex items-center space-x-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-emerald-400">{currentAge}</div>
                <div className="text-sm text-slate-400">years old</div>
              </div>
              <div className="w-px h-12 bg-slate-600" />
              <div className="text-center">
                <div className="text-2xl font-semibold text-white">Month {currentMonth}</div>
                <div className="text-sm text-slate-400">{currentStageData?.name}</div>
              </div>
            </div>

            {/* Health, Money & Controls - Right */}
            <div className="flex items-center space-x-3">
              {/* Health, Stress & Money Indicators */}
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2 bg-slate-700/50 rounded-xl px-4 py-2">
                  <Heart className="text-red-400" size={18} />
                  <span className="text-sm text-slate-300">Health</span>
                  <span className="text-sm font-bold text-red-400">{stats.health}</span>
                </div>
                {/* Stress Indicator - Always visible */}
                <div className={`flex items-center space-x-2 rounded-xl px-4 py-2 ${
                  stats.stress === 0 ? 'bg-green-500/20 border border-green-500/30' :
                  stats.stress >= 100 ? 'bg-red-500/20 border border-red-500/30' :
                  'bg-orange-500/20 border border-orange-500/30'
                }`}>
                  <Brain className={
                    stats.stress === 0 ? "text-green-400" :
                    stats.stress >= 100 ? "text-red-400" :
                    "text-orange-400"
                  } size={18} />
                  <span className="text-sm text-slate-300">Stress</span>
                  <span className={`text-sm font-bold ${
                    stats.stress === 0 ? 'text-green-400' :
                    stats.stress >= 100 ? 'text-red-400' :
                    'text-orange-400'
                  }`}>{stats.stress}</span>
                </div>
                
                <div className={`flex items-center space-x-2 rounded-xl px-4 py-2 ${
                  money < 0 ? 'bg-red-500/20 border border-red-500/30' : 'bg-slate-700/50'
                }`}>
                  <DollarSign className={money < 0 ? "text-red-400" : "text-emerald-400"} size={18} />
                  <span className="text-sm text-slate-300">Money</span>
                  <span className={`text-sm font-bold ${
                    money < 0 ? 'text-red-400' : 'text-emerald-400'
                  }`}>
                    {money < 0 ? `-$${Math.abs(money).toLocaleString()}` : `$${money.toLocaleString()}`}
                  </span>
                </div>
              </div>

              {/* Control Buttons */}
              <div className="flex items-center space-x-3">
                <motion.button
                  onClick={isTimeRunning ? pauseTime : startTime}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                    isTimeRunning 
                      ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white shadow-lg shadow-red-500/25' 
                      : 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white shadow-lg shadow-emerald-500/25'
                  }`}
                >
                  {isTimeRunning ? <Pause size={20} /> : <Play size={20} />}
                  <span className="text-sm font-bold">{isTimeRunning ? 'Pause' : 'Resume'}</span>
                </motion.button>
                
                <motion.button
                  onClick={() => {
                    const currentSpeed = useGameStore.getState().timeSpeed;
                    const newSpeed = currentSpeed >= 10 ? 1 : currentSpeed + 1;
                    useGameStore.getState().updateTimeSpeed(newSpeed);
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-blue-500/25"
                >
                  <Zap size={20} />
                  <span className="text-sm font-bold">Speed: {timeSpeed}x</span>
                </motion.button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Professional Timeline */}
      <div className="px-6 py-4">
        <ProfessionalTimeline />
      </div>

      {/* Decision Alert Banner */}
      {(availableDecisionsData.length > 0 || mandatoryDecisionsData.length > 0) && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mx-6 mb-4"
        >
          <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-2xl p-4 backdrop-blur-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                  <Target className="text-amber-400" size={20} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-amber-100">
                    {mandatoryDecisionsData.length > 0 ? 'Mandatory Decision Required' : 'Decisions Available'}
                  </h3>
                  <p className="text-amber-200/80 text-sm">
                    {mandatoryDecisionsData.length > 0 
                      ? 'You must make a decision before time can continue.'
                      : 'Make your choices to continue your life journey.'
                    }
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-amber-400 rounded-full animate-pulse" />
                <span className="text-amber-200 text-sm font-medium">Time Paused</span>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Main Content Area - Three Column Layout */}
      <div className="flex gap-6 px-6 pb-6">
        {/* Left Side - Decisions */}
        <div className="flex-1">
          {/* Decisions Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold text-white mb-1">
                  {mandatoryDecisionsData.length > 0 ? 'Mandatory Decision' : 'Available Decisions'}
                </h2>
                <p className="text-slate-400">
                  {mandatoryDecisionsData.length > 0 
                    ? '1 mandatory decision'
                    : `${availableDecisionsData.length} decision${availableDecisionsData.length !== 1 ? 's' : ''} available`
                  }
                </p>
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'all', name: 'All', icon: Target, color: 'from-slate-500 to-slate-600' },
                { id: 'health', name: 'Health', icon: Heart, color: 'from-red-500 to-pink-500' },
                { id: 'career', name: 'Career', icon: Briefcase, color: 'from-blue-500 to-cyan-500' },
                { id: 'relationships', name: 'Social', icon: Users, color: 'from-purple-500 to-indigo-500' },
                { id: 'education', name: 'Education', icon: GraduationCap, color: 'from-violet-500 to-purple-500' },
                { id: 'finance', name: 'Finance', icon: DollarSign, color: 'from-emerald-500 to-green-500' },
                { id: 'lifestyle', name: 'Lifestyle', icon: Brain, color: 'from-orange-500 to-yellow-500' },
              ].map((category) => {
                const Icon = category.icon;
                return (
                  <motion.button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg font-medium transition-all duration-300 ${
                      selectedCategory === category.id
                        ? 'bg-gradient-to-r ' + category.color + ' text-white shadow-lg'
                        : 'bg-slate-700/50 text-slate-300 hover:bg-slate-600/50'
                    }`}
                  >
                    <Icon size={16} />
                    <span>{category.name}</span>
                  </motion.button>
                );
              })}
            </div>
          </div>

          {/* Decisions Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <AnimatePresence>
              {(mandatoryDecisionsData.length > 0 ? mandatoryDecisionsData : filteredDecisions).map((decision, index) => (
                <motion.div
                  key={decision.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className={`${
                    mandatoryDecisionsData.length > 0 
                      ? 'ring-2 ring-red-500/50 shadow-red-500/20' 
                      : ''
                  }`}
                >
                  <DecisionCard decision={decision} />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Empty State */}
          {filteredDecisions.length === 0 && mandatoryDecisionsData.length === 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="flex flex-col items-center justify-center py-16 text-center"
            >
              <div className="w-20 h-20 bg-slate-700/50 rounded-2xl flex items-center justify-center mb-4">
                <Target size={40} className="text-slate-400" />
              </div>
              <h3 className="text-xl font-semibold text-slate-300 mb-2">No Decisions Available</h3>
              <p className="text-slate-500 text-sm max-w-md">
                {selectedCategory === 'all' 
                  ? "You've completed all available decisions for this life stage. Advance your age to unlock new opportunities."
                  : `No ${selectedCategory} decisions available right now.`
                }
              </p>
            </motion.div>
          )}
        </div>

        {/* Middle - Recent Events & Notifications (1/3 width) */}
        <div className="w-1/3 space-y-6">
          {/* Recent Events */}
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center">
              <Clock className="mr-2" />
              Recent Events
            </h3>
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {timeline.slice(-6).reverse().map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="flex items-start space-x-3 p-3 bg-slate-700/30 rounded-lg hover:bg-slate-700/50 transition-colors"
                >
                  <div
                    className="w-3 h-3 rounded-full mt-1.5 flex-shrink-0"
                    style={{ backgroundColor: event.color }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white truncate">
                      {event.title}
                    </div>
                    <div className="text-xs text-slate-400 mb-1">
                      Age {event.age} • {event.type}
                    </div>
                    {event.description && (
                      <div className="text-xs text-slate-500 line-clamp-2">
                        {event.description}
                      </div>
                    )}
                    {/* Impact Stats */}
                    {event.impact && Object.keys(event.impact).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {Object.entries(event.impact).slice(0, 3).map(([stat, change]) => (
                          <span
                            key={stat}
                            className={`text-xs px-2 py-1 rounded ${
                              change && change > 0
                                ? 'bg-emerald-500/20 text-emerald-400'
                                : change && change < 0
                                ? 'bg-red-500/20 text-red-400'
                                : 'bg-slate-500/20 text-slate-400'
                            }`}
                          >
                            {stat}: {change && change > 0 ? '+' : ''}{change}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
              {timeline.length === 0 && (
                <div className="text-center py-8">
                  <div className="w-12 h-12 bg-slate-700/50 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Clock size={24} className="text-slate-400" />
                  </div>
                  <p className="text-slate-500 text-sm">No events yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Notifications - Always Visible */}
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center">
              <Clock className="mr-2" />
              Notifications
            </h3>
            <NotificationCenter />
          </div>

          {/* Financial Management */}
          <FinancialManagement />
        </div>

        {/* Right Side - Status & Quick Actions */}
        <div className="w-80 space-y-6">
          {/* Character Avatar */}
          <CharacterAvatar 
            currentAge={currentAge}
            stats={stats}
            status={status}
            currentStage={currentStage}
            recentEvents={timeline.slice(-3).map(event => event.title)}
          />

          {/* Status Panel */}
          <StatusPanel />


          {/* Quick Actions */}
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center">
              <Zap className="mr-2" />
              Quick Actions
            </h3>
            <div className="space-y-3">
              <motion.button
                onClick={isTimeRunning ? pauseTime : startTime}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`w-full flex items-center justify-center space-x-2 px-4 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  isTimeRunning 
                    ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white shadow-lg shadow-red-500/25' 
                    : 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white shadow-lg shadow-emerald-500/25'
                }`}
              >
                {isTimeRunning ? <Pause size={18} /> : <Play size={18} />}
                <span>{isTimeRunning ? 'Pause' : 'Resume'} Time</span>
              </motion.button>
              
              <motion.button
                onClick={() => {
                  const currentSpeed = useGameStore.getState().timeSpeed;
                  const newSpeed = currentSpeed >= 10 ? 1 : currentSpeed + 1;
                  useGameStore.getState().updateTimeSpeed(newSpeed);
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-blue-500/25"
              >
                <Zap size={18} />
                <span>Speed: {timeSpeed}x</span>
              </motion.button>
            </div>
          </div>
        </div>
      </div>

      {/* Event Modal */}
      <EventModal 
        event={currentEvent}
        isOpen={showEventModal}
        onClose={() => {
          setShowEventModal(false);
          setCurrentEvent(null);
        }}
      />

    </div>
  );
};

export default NewDashboard;
